import api from '@/api/api';
import { createQueryParams } from '@/utils/createQueryParams';

const DOMAIN = '/user';

/** Alinhado a `CreateUserDto` no backend (whitelist + forbidNonWhitelisted). */
const USER_CREATE_KEYS = [
	'email',
	'password',
	'first_name',
	'last_name',
	'cpf',
	'phone_number',
	'birthday',
] as const satisfies ReadonlyArray<keyof Users.Create>;

/** Alinhado a `CompleteUserDto` no backend (PATCH `:id/complete`). */
const USER_COMPLETE_PROFILE_KEYS = [
	'first_name',
	'last_name',
	'cpf',
	'password',
	'phone_number',
	'birthday',
] as const;

function pickCreateUserBody(
	body: Partial<Users.Create> & Record<string, unknown>,
): Users.Create {
	const out = {} as Users.Create;
	for (const key of USER_CREATE_KEYS) {
		const v = body[key];
		if (v !== undefined) (out as Record<string, unknown>)[key] = v;
	}
	return out;
}

function pickCompleteProfileBody(
	body: Record<string, unknown>,
): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	for (const key of USER_COMPLETE_PROFILE_KEYS) {
		if (body[key] !== undefined) out[key] = body[key];
	}
	return out;
}

const get = async (
	params: Users.UserParams,
): Promise<Users.GetUsersRespose> => {
	const queryParams = createQueryParams({ ...params });
	const { data } = await api.get(`${DOMAIN}?${queryParams}`);
	return data;
};

const getById = async (params: Users.GetDetail): Promise<Users.User> => {
	const url = params.isMe ? `${DOMAIN}/me` : `${DOMAIN}/${params.id}`;
	const { data } = await api.get(url);
	return data;
};

const create = async (body: Users.Create): Promise<Users.CreateResponse> => {
	const { data } = await api.post(`${DOMAIN}`, pickCreateUserBody(body));
	return data;
};

const patch = async (
	newData: Partial<Users.Update> & { id: string },
): Promise<Users.User> => {
	const { id, ...body } = newData;
	const { data } = await api.patch(`${DOMAIN}/${id}`, body);
	return data;
};

const patchBanned = async (
	userData: Users.PatchBanned,
): Promise<Users.User> => {
	const { data } = await api.delete(`${DOMAIN}/${userData.id}`, {
		data: userData.body,
	});
	return data;
};

const patchComplement = async (
	complementData: Users.CreateComplement,
): Promise<Users.CreateResponse> => {
	const { id, ...rest } = complementData;
	const { data } = await api.patch(
		`${DOMAIN}/${id}/complete`,
		pickCompleteProfileBody(rest as Record<string, unknown>),
	);
	return data;
};

const login = async (body: Users.Login): Promise<Users.CreateResponse> => {
	const { data } = await api.post(`${DOMAIN}/login`, body);
	return data;
};

const logout = async (): Promise<void> => {
	await api.delete(`${DOMAIN}/logout`);
};

const patchResetCredits = async (id: string): Promise<void> => {
	await api.patch(`${DOMAIN}/${id}/reset-user-credits`);
};

const UserService = {
	get,
	getById,
	create,
	patch,
	patchBanned,
	patchComplement,
	login,
	logout,
	patchResetCredits,
};

export { UserService };
